import { Button, Checkbox, Grid, ScrollArea, Stack, Title } from '@mantine/core';
import React, { useEffect } from 'react';

import { TSDData } from '@e-origin/contracts';
import { TSDType } from '@e-origin/shared';

import { DateTimePickerInput, HideElementFor, LeavePageNotification, StickyContainer, TextInput } from '@components';
import { TSDArrivalTransportMeansSection } from '@features/tsd/TSDView/TSDViewGeneralInfo/TSDArrivalTransportMeansSection';
import { TSDCustomerSection } from '@features/tsd/TSDView/TSDViewGeneralInfo/TSDCustomerSection';
import { TSDDeclarantInfo } from '@features/tsd/TSDView/TSDViewGeneralInfo/TSDDeclarantInfo';
import { TSDLocationSection } from '@features/tsd/TSDView/TSDViewGeneralInfo/TSDLocationSection';
import { TSDPlaceOfUnloadingSection } from '@features/tsd/TSDView/TSDViewGeneralInfo/TSDPlaceOfUnloadingSection';
import { TSDRepresentativeSection } from '@features/tsd/TSDView/TSDViewGeneralInfo/TSDRepresentativeSection';
import { TSDWarehouseSection } from '@features/tsd/TSDView/TSDViewGeneralInfo/TSDWarehouseSection';
import { useTsdViewGeneralInfoFormInitializer } from '@features/tsd/TSDView/TSDViewGeneralInfo/useTSDViewGeneralInfoFormInitializer';

interface TSDViewGeneralInfoProps {
  tsd: TSDData;
  disabled: boolean;
}

export const TSDViewGeneralInfo: React.FC<TSDViewGeneralInfoProps> = ({ tsd, disabled }) => {
  const { form, onSubmit, buildFormValues } = useTsdViewGeneralInfoFormInitializer({
    tsd,
  });

  useEffect(() => {
    form.setValues(buildFormValues(tsd));
    form.resetDirty({});
  }, [tsd]);

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <ScrollArea h="100%" offsetScrollbars="y">
        <form onSubmit={form.onSubmit((values) => onSubmit(values))}>
          <Stack mx="xl">
            <Stack mb="xl">
              <Title order={5} fw={600}>
                General
              </Title>

              <Grid align="center">
                <Grid.Col span={3}>
                  <TextInput
                    {...form.getInputProps('supervisingCustomsOffice.referenceNumber')}
                    label="Supervising customs office"
                    disabled={disabled}
                    data-pw="supervising-customs-office-reference-number"
                  />
                </Grid.Col>
                <HideElementFor condition={() => tsd.generalInfo?.type !== TSDType.TRANSFERRED}>
                  <Grid.Col span={4}>
                    <TextInput
                      {...form.getInputProps('personNotifyingTheArrivalAfterMovement.identificationNumber')}
                      label="Person notifying the arrival after movement"
                      disabled={disabled}
                    />
                  </Grid.Col>
                </HideElementFor>

                <HideElementFor condition={() => tsd.generalInfo?.type === TSDType.TRANSFERRED}>
                  <>
                    <Grid.Col span={2.5}>
                      <DateTimePickerInput {...form.getInputProps('generalInfo.declarationDate')} label="Declaration date" disabled={disabled} />
                    </Grid.Col>

                    <Grid.Col span={2}>
                      <Checkbox
                        {...form.getInputProps('generalInfo.ensReUseIndicator', { type: 'checkbox' })}
                        label="ENS re-use"
                        disabled={disabled || tsd.generalInfo?.isAmended}
                      />
                    </Grid.Col>
                  </>
                </HideElementFor>
              </Grid>
            </Stack>

            <TSDLocationSection form={form} disabled={disabled} tsdType={tsd.generalInfo?.type} />

            <TSDArrivalTransportMeansSection form={form} disabled={disabled} />

            <TSDWarehouseSection form={form} disabled={disabled} />

            <TSDPlaceOfUnloadingSection form={form} disabled={disabled} />

            <TSDCustomerSection form={form} disabled={disabled} />

            <TSDDeclarantInfo form={form} disabled={disabled} />

            <TSDRepresentativeSection form={form} disabled={disabled} />
          </Stack>
          <StickyContainer>
            <Button type="submit" disabled={disabled || !form.isValid()} variant="filled" size="lg" data-pw="save-tsd-general-info-btn">
              Save
            </Button>
          </StickyContainer>
        </form>
      </ScrollArea>
    </>
  );
};
